import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { useToast } from "@/hooks/use-toast";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { MediaBrowser } from "@/components/media/MediaBrowser";
import { Image as ImageIcon, X, CalendarIcon } from "lucide-react";
import { format } from "date-fns";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { cn } from "@/lib/utils";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";

const categories = [
  "Pressure Washing Tips",
  "Window Cleaning",
  "Carpet Care",
  "Gutter Maintenance",
  "Solar Panel Care",
  "Soft Wash Services",
  "Maintenance Guides",
  "Local News",
  "Industry Insights",
];

interface BlogPostFormProps {
  initialData?: {
    id?: string;
    title: string;
    slug: string;
    excerpt: string;
    content: string;
    featuredImage?: string;
    metaDescription: string;
    metaKeywords?: string;
    category: string;
    isPublished: boolean;
    scheduledPublishAt?: string;
  };
}

export const BlogPostForm = ({ initialData }: BlogPostFormProps) => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const queryClient = useQueryClient();

  // Store the post ID in state to ensure it persists through the component lifecycle
  const [postId] = useState(initialData?.id);

  const [formData, setFormData] = useState({
    title: initialData?.title || "",
    slug: initialData?.slug || "",
    excerpt: initialData?.excerpt || "",
    content: initialData?.content || "",
    featuredImage: initialData?.featuredImage || "",
    metaDescription: initialData?.metaDescription || "",
    metaKeywords: initialData?.metaKeywords || "",
    category: initialData?.category || categories[0],
    isPublished: initialData?.isPublished || false,
  });

  const [mediaBrowserOpen, setMediaBrowserOpen] = useState(false);
  const [selectedMediaId, setSelectedMediaId] = useState<string | null>(null);
  
  // Determine initial publish status
  const getInitialPublishStatus = () => {
    if (initialData?.scheduledPublishAt) return "scheduled";
    if (initialData?.isPublished) return "published";
    return "draft";
  };
  
  const [publishStatus, setPublishStatus] = useState<"draft" | "published" | "scheduled">(
    getInitialPublishStatus()
  );
  const [scheduledDate, setScheduledDate] = useState<Date | undefined>(
    initialData?.scheduledPublishAt ? new Date(initialData.scheduledPublishAt) : undefined
  );

  const saveMutation = useMutation({
    mutationFn: async () => {
      const postData = {
        title: formData.title,
        slug: formData.slug,
        excerpt: formData.excerpt,
        content: formData.content,
        featured_image: formData.featuredImage || null,
        meta_description: formData.metaDescription,
        meta_keywords: formData.metaKeywords || null,
        category: formData.category,
        is_published: publishStatus === "published",
        scheduled_publish_at: publishStatus === "scheduled" ? scheduledDate?.toISOString() : null,
      };

      let returnedPostId: string;

      // Use postId from state to ensure we're updating existing posts
      if (postId) {
        console.log('Updating existing post:', postId, 'with slug:', formData.slug);
        const { error } = await supabase
          .from("blog_posts")
          .update(postData)
          .eq("id", postId);
        if (error) throw error;
        returnedPostId = postId;
      } else {
        console.log('Creating new post with slug:', formData.slug);
        const { data, error } = await supabase
          .from("blog_posts")
          .insert([postData])
          .select()
          .single();
        if (error) throw error;
        returnedPostId = data.id;
      }

      // Track media usage if featured image is set
      if (selectedMediaId && returnedPostId) {
        // Delete old usage records for this post
        await supabase
          .from('media_usage')
          .delete()
          .eq('blog_post_id', returnedPostId)
          .eq('usage_type', 'featured_image');

        // Create new usage record
        const { error: usageError } = await supabase
          .from('media_usage')
          .insert({
            media_id: selectedMediaId,
            blog_post_id: returnedPostId,
            usage_type: 'featured_image'
          });

        if (usageError) console.error('Failed to track media usage:', usageError);
      }

      return returnedPostId;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["blog-posts"] });
      toast({
        title: "Success",
        description: `Blog post ${postId ? "updated" : "created"} successfully!`,
      });
      navigate("/admin/blog");
    },
    onError: (error) => {
      toast({
        title: "Error",
        description: `Failed to save post: ${error.message}`,
        variant: "destructive",
      });
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    saveMutation.mutate();
  };

  const generateSlug = (title: string) => {
    return title
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/(^-|-$)/g, "");
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <div>
        <Label htmlFor="title">Title *</Label>
        <Input
          id="title"
          value={formData.title}
          onChange={(e) => {
            setFormData({
              ...formData,
              title: e.target.value,
              slug: generateSlug(e.target.value),
            });
          }}
          required
        />
      </div>

      <div>
        <Label htmlFor="slug">URL Slug *</Label>
        <Input
          id="slug"
          value={formData.slug}
          onChange={(e) => setFormData({ ...formData, slug: e.target.value })}
          required
        />
        <p className="text-sm text-muted-foreground mt-1">
          URL: /blog/{formData.slug}
        </p>
      </div>

      <div>
        <Label htmlFor="category">Category *</Label>
        <Select
          value={formData.category}
          onValueChange={(value) =>
            setFormData({ ...formData, category: value })
          }
        >
          <SelectTrigger>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {categories.map((cat) => (
              <SelectItem key={cat} value={cat}>
                {cat}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div>
        <Label htmlFor="excerpt">Excerpt *</Label>
        <Textarea
          id="excerpt"
          value={formData.excerpt}
          onChange={(e) =>
            setFormData({ ...formData, excerpt: e.target.value })
          }
          rows={3}
          required
        />
        <p className="text-sm text-muted-foreground mt-1">
          Brief summary for the blog listing page
        </p>
      </div>

      <div>
        <Label htmlFor="content">Content *</Label>
        <Textarea
          id="content"
          value={formData.content}
          onChange={(e) =>
            setFormData({ ...formData, content: e.target.value })
          }
          rows={15}
          required
        />
        <p className="text-sm text-muted-foreground mt-1">
          Full Markdown & HTML supported: **bold**, *italic*, [links](url), ![images](url), lists, code blocks, tables, etc.
        </p>
      </div>

      <div>
        <Label>Featured Image</Label>
        {formData.featuredImage ? (
          <div className="relative mt-2">
            <img
              src={formData.featuredImage}
              alt="Featured"
              className="w-full h-48 object-cover rounded-lg border"
            />
            <Button
              type="button"
              variant="destructive"
              size="icon"
              className="absolute top-2 right-2"
              onClick={() => {
                setFormData({ ...formData, featuredImage: '' });
                setSelectedMediaId(null);
              }}
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
        ) : (
          <Button
            type="button"
            variant="outline"
            className="w-full mt-2"
            onClick={() => setMediaBrowserOpen(true)}
          >
            <ImageIcon className="mr-2 h-4 w-4" />
            Select Featured Image
          </Button>
        )}
        <Input
          id="featuredImage"
          type="url"
          value={formData.featuredImage}
          onChange={(e) =>
            setFormData({ ...formData, featuredImage: e.target.value })
          }
          placeholder="Or paste image URL..."
          className="mt-2"
        />
      </div>

      <div>
        <Label htmlFor="metaDescription">Meta Description *</Label>
        <Textarea
          id="metaDescription"
          value={formData.metaDescription}
          onChange={(e) =>
            setFormData({ ...formData, metaDescription: e.target.value })
          }
          rows={2}
          maxLength={160}
          required
        />
        <p className="text-sm text-muted-foreground mt-1">
          {formData.metaDescription.length}/160 characters (for SEO)
        </p>
      </div>

      <div>
        <Label htmlFor="metaKeywords">Meta Keywords</Label>
        <Input
          id="metaKeywords"
          value={formData.metaKeywords}
          onChange={(e) =>
            setFormData({ ...formData, metaKeywords: e.target.value })
          }
          placeholder="pressure washing, ventura, cleaning tips"
        />
      </div>

      <div className="space-y-4">
        <Label>Publishing Status</Label>
        <RadioGroup value={publishStatus} onValueChange={(value: any) => setPublishStatus(value)}>
          <div className="flex items-center space-x-2">
            <RadioGroupItem value="draft" id="draft" />
            <Label htmlFor="draft" className="font-normal cursor-pointer">
              Draft - Save without publishing
            </Label>
          </div>
          <div className="flex items-center space-x-2">
            <RadioGroupItem value="published" id="published" />
            <Label htmlFor="published" className="font-normal cursor-pointer">
              Publish Now - Make live immediately
            </Label>
          </div>
          <div className="flex items-center space-x-2">
            <RadioGroupItem value="scheduled" id="scheduled" />
            <Label htmlFor="scheduled" className="font-normal cursor-pointer">
              Schedule - Publish automatically at a specific date/time
            </Label>
          </div>
        </RadioGroup>

        {publishStatus === "scheduled" && (
          <div className="ml-6">
            <Label>Scheduled Publish Date & Time</Label>
            <Popover>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  className={cn(
                    "w-full justify-start text-left font-normal mt-2",
                    !scheduledDate && "text-muted-foreground"
                  )}
                >
                  <CalendarIcon className="mr-2 h-4 w-4" />
                  {scheduledDate ? format(scheduledDate, "PPP 'at' p") : "Pick a date and time"}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0" align="start">
                <Calendar
                  mode="single"
                  selected={scheduledDate}
                  onSelect={setScheduledDate}
                  disabled={(date) => date < new Date()}
                  initialFocus
                  className="pointer-events-auto"
                />
                {scheduledDate && (
                  <div className="p-3 border-t">
                    <Label className="text-sm">Time</Label>
                    <Input
                      type="time"
                      value={scheduledDate ? format(scheduledDate, "HH:mm") : ""}
                      onChange={(e) => {
                        if (scheduledDate) {
                          const [hours, minutes] = e.target.value.split(":");
                          const newDate = new Date(scheduledDate);
                          newDate.setHours(parseInt(hours), parseInt(minutes));
                          setScheduledDate(newDate);
                        }
                      }}
                      className="mt-1"
                    />
                  </div>
                )}
              </PopoverContent>
            </Popover>
            {scheduledDate && (
              <p className="text-sm text-muted-foreground mt-2">
                Will publish on {format(scheduledDate, "PPPP 'at' p")}
              </p>
            )}
          </div>
        )}
      </div>

      <div className="flex gap-4">
        <Button type="submit" disabled={saveMutation.isPending}>
          {saveMutation.isPending ? "Saving..." : "Save Post"}
        </Button>
        <Button
          type="button"
          variant="outline"
          onClick={() => navigate("/admin/blog")}
        >
          Cancel
        </Button>
      </div>

      <MediaBrowser
        open={mediaBrowserOpen}
        onOpenChange={setMediaBrowserOpen}
        onSelect={(imageUrl, media) => {
          setFormData({ ...formData, featuredImage: imageUrl });
          setSelectedMediaId(media.id);
        }}
      />
    </form>
  );
};
